import { Check, ChevronsUpDown, Combine } from 'lucide-react'
import { useState } from 'react'
import { Link, useParams } from 'react-router'
import { SidebarMenuButton } from '@/components/sv/sidebar'
import { BreadcrumbItem } from '@/components/ui/breadcrumb'
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { gql } from '@/lib/graphql/gql'
import { useSuspenseQuery, useIdCompany, useCompanyPath } from '@/lib/hooks'
import { cn } from '@/lib/utils'
import type {
  PipelineSwitcherQuery,
  PipelineSwitcherQueryVariables,
} from '@/components/layout/breadcrumbs/app/pipeline-switcher/switcher.types.gen'

const PIPELINE_SWITCHER_QUERY = gql`
  query PipelineSwitcher($idCompany: String!) {
    company(id: $idCompany) {
      id
      pipelines @live {
        id
        total
        items {
          id
          displayName
        }
      }
    }
  }
`

export const PipelineSwitcherData = () => {
  const { getCompanyPath } = useCompanyPath()
  const [open, setOpen] = useState(false)
  const { idPipeline = '' } = useParams()
  const { idCompany } = useIdCompany()

  const { data } = useSuspenseQuery<
    PipelineSwitcherQuery,
    PipelineSwitcherQueryVariables
  >(PIPELINE_SWITCHER_QUERY, {
    variables: { idCompany },
  })

  const pipelines = data?.company?.pipelines?.items ?? []

  const activePipeline =
    pipelines.find((pipeline) => pipeline.id === idPipeline) || pipelines[0]

  return (
    <BreadcrumbItem>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild className="cursor-pointer">
          <SidebarMenuButton
            size="lg"
            aria-expanded={open}
            className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground cursor-pointer"
          >
            <div className="grid flex-1 text-left text-sm leading-tight">
              <span
                title={activePipeline?.displayName ?? ''}
                className="max-w-[250px] truncate text-foreground font-medium"
              >
                {activePipeline?.displayName ?? ''}
              </span>
            </div>
            <ChevronsUpDown className="ml-auto" />
          </SidebarMenuButton>
        </PopoverTrigger>
        <PopoverContent
          className="w-max min-w-[max(250px,var(--radix-popper-anchor-width))] max-w-[480px] p-0"
          align="start"
          side="bottom"
          sideOffset={4}
        >
          <Command>
            <CommandInput placeholder="Search pipeline..." className="h-9" />
            <CommandList>
              <CommandEmpty>No pipeline found</CommandEmpty>
              <CommandGroup>
                {pipelines.map((pipeline) => (
                  <Link
                    key={pipeline.id}
                    to={getCompanyPath({
                      pathname: '/pipeline/:idPipeline',
                      variables: { idPipeline: pipeline.id },
                    })}
                    onClick={() => setOpen(false)}
                  >
                    <CommandItem
                      value={pipeline.id}
                      keywords={[pipeline.displayName]}
                      className="cursor-pointer"
                    >
                      <div className="flex size-6 items-center justify-center rounded-md border">
                        {activePipeline?.id === pipeline.id ? (
                          <Check className="size-3.5 shrink-0" />
                        ) : (
                          <Combine className="size-3.5 shrink-0" />
                        )}
                      </div>
                      <span
                        className={cn('truncate', {
                          'font-medium': activePipeline?.id === pipeline?.id,
                        })}
                      >
                        {pipeline.displayName}
                      </span>
                    </CommandItem>
                  </Link>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </BreadcrumbItem>
  )
}
